"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Sidebar } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { MobileNav } from "@/components/layout/MobileNav";
import { BrandingProvider } from "@/components/layout/BrandingProvider";
import { QuickActionsProvider, CommandPaletteProvider } from "@/components/modules/quick-actions";
import { WorkflowWizardProvider } from "@/components/modules/workflow";
import { OnboardingProvider, OnboardingWizard } from "@/components/onboarding";
import { useOnboarding } from "@/hooks/useOnboarding";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { TourProvider } from "@/contexts/TourContext";
import { KeyboardShortcutsDialog, useKeyboardShortcuts } from "@/components/accessibility/KeyboardShortcuts";
import { ErrorBoundary, ComponentErrorBoundary } from "@/components/error-boundary";
import { SubscriptionGuard } from "@/components/guards/SubscriptionGuard";
import { logger } from '@/lib/logger';

interface User {
  name: string;
  email: string;
  image: string | null;
  role: string;
  organizationId?: string;
  hasCompletedOnboarding?: boolean;
  createdAt?: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  primaryColor: string;
  secondaryColor: string | null;
  // Feature flags
  bookingEnabled: boolean;
  gpsTrackingEnabled: boolean;
  timeTrackingEnabled: boolean;
  smsEnabled: boolean;
  priceBookEnabled: boolean;
  aiAgentEnabled: boolean;
  // Staff permissions
  staffCanViewAllJobs: boolean;
  staffCanViewFinancials: boolean;
}

// Inner component that uses the onboarding context
function DashboardContent({
  children,
  user,
  organization,
  mobileNavOpen,
  setMobileNavOpen,
}: {
  children: React.ReactNode;
  user: User;
  organization: Organization | null;
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
}) {
  const { showOnboarding, completeOnboarding, skipOnboarding } = useOnboarding();
  const { isOpen: shortcutsOpen, closeShortcuts } = useKeyboardShortcuts();

  return (
    <>
      {/* Keyboard Shortcuts Dialog */}
      <KeyboardShortcutsDialog isOpen={shortcutsOpen} onClose={closeShortcuts} />

      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
      >
        Skip to main content
      </a>

      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard
          isOpen={showOnboarding}
          onComplete={completeOnboarding}
          onSkip={skipOnboarding}
        />
      )}

      <div className="flex min-h-screen">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block">
          <ComponentErrorBoundary>
            <Sidebar
              organizationName={organization?.name || "Organization"}
              organizationLogo={organization?.logo || null}
              userRole={user.role}
              primaryColor={organization?.primaryColor || "#3b82f6"}
            />
          </ComponentErrorBoundary>
        </aside>

        {/* Mobile Navigation */}
        <ComponentErrorBoundary>
          <MobileNav
            isOpen={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            organizationName={organization?.name || "Organization"}
            organizationLogo={organization?.logo || null}
            userRole={user.role}
            primaryColor={organization?.primaryColor || "#3b82f6"}
          />
        </ComponentErrorBoundary>

        {/* Main Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <ComponentErrorBoundary>
            <Header
              userName={user.name}
              userEmail={user.email}
              userImage={user.image}
              userRole={user.role}
              hasCompletedOnboarding={user.hasCompletedOnboarding}
              createdAt={user.createdAt}
              onMenuClick={() => setMobileNavOpen(true)}
            />
          </ComponentErrorBoundary>

          <main id="main-content" className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900" tabIndex={-1}>
            <div className="container mx-auto p-4 sm:p-6">
              <ErrorBoundary level="page">
                {children}
              </ErrorBoundary>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Fetch user session data
    fetch("/api/auth/session")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch session");
        return res.json();
      })
      .then((data) => {
        if (data.user) {
          setUser(data.user);
          // Fetch organization data if user has organizationId
          if (data.user.organizationId) {
            return fetch(`/api/organizations/${data.user.organizationId}`);
          }
        } else {
          // No user in session - redirect to login
          router.push("/login");
        }
      })
      .then((res) => {
        if (res && res.ok) {
          return res.json();
        }
        return null;
      })
      .then((orgData) => {
        if (orgData) {
          setOrganization(orgData);
        }
      })
      .catch((err) => {
        logger.error("Error fetching session data:", { error: err });
      })
      .finally(() => {
        setLoading(false);
        setAuthChecked(true);
      });
  }, [router]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center" role="status" aria-live="polite">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" aria-hidden="true" />
          <p className="mt-2 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && authChecked) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Your session has expired. Please log in again.</p>
          <a
            href="/login"
            className="inline-flex items-center px-4 py-2 bg-info text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  // Still loading or redirecting
  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center" role="status" aria-live="polite">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" aria-hidden="true" />
          <p className="mt-2 text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // If organization is not loaded, show a loading state
  if (!organization) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center" role="status" aria-live="polite">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" aria-hidden="true" />
          <p className="mt-2 text-gray-500">Loading organization...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary level="root">
      <OrganizationProvider
        value={{
          id: organization.id,
          name: organization.name,
          slug: organization.slug,
          logo: organization.logo,
          primaryColor: organization.primaryColor,
          secondaryColor: organization.secondaryColor,
          features: {
            bookingEnabled: organization.bookingEnabled,
            gpsTrackingEnabled: organization.gpsTrackingEnabled,
            timeTrackingEnabled: organization.timeTrackingEnabled,
            smsEnabled: organization.smsEnabled,
            priceBookEnabled: organization.priceBookEnabled,
            aiAgentEnabled: organization.aiAgentEnabled,
          },
          staffPermissions: {
            staffCanViewAllJobs: organization.staffCanViewAllJobs ?? false,
            staffCanViewFinancials: organization.staffCanViewFinancials ?? false,
          },
        }}
      >
        <BrandingProvider organizationId={user.organizationId}>
          <QuickActionsProvider>
          <CommandPaletteProvider>
            <WorkflowWizardProvider>
              <OnboardingProvider>
                <TourProvider>
                  <SubscriptionGuard>
                    <DashboardContent
                      user={user}
                      organization={organization}
                      mobileNavOpen={mobileNavOpen}
                      setMobileNavOpen={setMobileNavOpen}
                    >
                      {children}
                    </DashboardContent>
                  </SubscriptionGuard>
                </TourProvider>
              </OnboardingProvider>
            </WorkflowWizardProvider>
          </CommandPaletteProvider>
          </QuickActionsProvider>
        </BrandingProvider>
      </OrganizationProvider>
    </ErrorBoundary>
  );
}
